import React from 'react';
import { useCachedFetch } from '../lib/cache';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Users, Calendar, CheckSquare, FileSignature, DollarSign, Clock, TrendingDown, Plus, Receipt, ArrowRight, UserPlus, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CreatedByBadge } from '../components/CreatedByBadge';
import usePlanFeatures from '../hooks/usePlanFeatures';
import UpgradeNudge from '../components/UpgradeNudge';
import AiCashflowCard from '../components/AiCashflowCard';
import CashFlowWidget from '../components/CashFlowWidget';
import SmsAnalyticsWidget from '../components/SmsAnalyticsWidget';
import A2pApprovalBanner from '../components/A2pApprovalBanner';

export default function DashboardPage() {
  const { data: stats } = useCachedFetch('/dashboard/stats');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { has, loading: featuresLoading } = usePlanFeatures();
  // While features load, optimistically show everything — same approach
  // the Sidebar uses; the backend still enforces real gating.
  const can = (key) => featuresLoading || has(key);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  if (!stats) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-[#B98A2C] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div data-testid="dashboard-page" className="animate-fade-in space-y-5">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#B98A2C] mb-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{greeting()}</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {user?.name || 'Welcome'}
          </h1>
        </div>
        <p className="text-[10px] text-[#758A9F] hidden sm:block">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
      </div>

      {/* A2P approval banner — only renders when the workspace's A2P
          campaign becomes VERIFIED for the first time. Dismissible. */}
      <A2pApprovalBanner />

      {/* Quick Actions — hidden per-tier so a Starter doesn't see "Add
          Expense" (bookkeeping) or an admin sees crew links they can't use */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {can('bookkeeping') && (
          <button data-testid="quick-add-expense" onClick={() => navigate('/bookkeeping?action=add&type=expense')}
            className="order-1 group bg-gradient-to-br from-[#1B2838] to-[#1f2f42] border border-[#B98A2C]/10 rounded-lg p-3.5 text-left hover:border-[#B98A2C]/40 transition-all active:scale-[0.98]">
            <div className="w-9 h-9 rounded-lg bg-red-500/10 text-red-400 flex items-center justify-center mb-2.5 group-hover:bg-red-500/20 transition-colors">
              <TrendingDown size={18} />
            </div>
            <p className="text-xs font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Add Expense</p>
            <p className="text-[9px] text-[#758A9F] mt-0.5">Quick receipt upload</p>
          </button>
        )}
        <button data-testid="quick-new-project" onClick={() => navigate('/projects/new')}
          className="order-1 sm:order-2 group bg-gradient-to-br from-[#CBA33B]/15 to-[#B98A2C]/10 border border-[#CBA33B]/35 rounded-lg p-3.5 text-left hover:border-[#CBA33B] hover:shadow-lg hover:shadow-[#CBA33B]/15 transition-all active:scale-[0.98]">
          <div className="w-9 h-9 rounded-lg bg-[#CBA33B]/20 text-[#CBA33B] flex items-center justify-center mb-2.5 group-hover:bg-[#CBA33B]/30 transition-colors">
            <Briefcase size={18} />
          </div>
          <p className="text-xs font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Create New Project</p>
          <p className="text-[9px] text-[#758A9F] mt-0.5">Client → estimate → schedule</p>
        </button>
        <button data-testid="quick-new-invoice" onClick={() => navigate('/envoice')}
          className="order-4 sm:order-3 group bg-gradient-to-br from-[#1B2838] to-[#1f2f42] border border-[#B98A2C]/10 rounded-lg p-3.5 text-left hover:border-[#B98A2C]/40 transition-all active:scale-[0.98]">
          <div className="w-9 h-9 rounded-lg bg-[#B98A2C]/10 text-[#CBA33B] flex items-center justify-center mb-2.5 group-hover:bg-[#B98A2C]/20 transition-colors">
            <FileText size={18} />
          </div>
          <p className="text-xs font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Quick Invoice</p>
          <p className="text-[9px] text-[#758A9F] mt-0.5">Standalone bill</p>
        </button>
        {can('documents') && (
          <button data-testid="quick-add-client" onClick={() => navigate('/clients?action=new')}
            className="order-2 sm:order-4 group bg-gradient-to-br from-[#1B2838] to-[#1f2f42] border border-[#B98A2C]/10 rounded-lg p-3.5 text-left hover:border-[#B98A2C]/40 transition-all active:scale-[0.98]">
            <div className="w-9 h-9 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center mb-2.5 group-hover:bg-blue-500/20 transition-colors">
              <UserPlus size={18} />
            </div>
            <p className="text-xs font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Add Client</p>
            <p className="text-[9px] text-[#758A9F] mt-0.5">New contact</p>
          </button>
        )}
      </div>

      {/* Plan upsell nudge — quiet card pointing at the next tier's
          biggest unlock. Self-hides on Pro and lifetime/trial. */}
      <UpgradeNudge />

      {/* AI cash-flow forecast — only renders for plans with AI access. */}
      <AiCashflowCard />

      {/* Cash-Flow Forecast (all plans) — estimates + open invoices bucketed 30/60/90. */}
      <CashFlowWidget />

      {/* SMS at-a-glance — sent/received/opt-outs/cost over the last 30 days. */}
      <SmsAnalyticsWidget />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <KpiCard icon={DollarSign} label="Revenue" value={`$${(stats.total_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} color="text-[#CBA33B]" bg="bg-[#B98A2C]/8" onClick={() => navigate('/envoice')} />
        <KpiCard icon={Clock} label="Outstanding" value={`$${(stats.pending_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} color="text-amber-400" bg="bg-amber-500/8" onClick={() => navigate('/envoice')} />
        <KpiCard icon={FileText} label="Invoices" value={stats.invoice_count || 0} color="text-blue-400" bg="bg-blue-500/8" onClick={() => navigate('/envoice')} />
        <KpiCard icon={Users} label="Clients" value={stats.client_count || 0} color="text-[#A8B8CA]" bg="bg-[#223347]/60" onClick={() => navigate('/files')} />
      </div>

      {(can('schedule') || can('documents')) && (
        <div className="grid grid-cols-3 gap-2.5">
          {can('schedule') && (
            <KpiCard icon={CheckSquare} label="Tasks" value={stats.task_count || 0} color="text-emerald-400" bg="bg-emerald-500/8" onClick={() => navigate('/schedule')} />
          )}
          {can('schedule') && (
            <KpiCard icon={Calendar} label="Events Today" value={stats.today_events || 0} color="text-[#CBA33B]" bg="bg-[#B98A2C]/8" onClick={() => navigate('/schedule')} />
          )}
          {can('documents') && (
            <KpiCard icon={FileSignature} label="Documents" value={stats.doc_count || 0} color="text-[#A8B8CA]" bg="bg-[#223347]/60" onClick={() => navigate('/files')} />
          )}
        </div>
      )}

      {/* Activity Grid — schedule cards hide for plans without /schedule */}
      <div className={`grid grid-cols-1 ${can('schedule') ? 'lg:grid-cols-3' : 'lg:grid-cols-1'} gap-3`}>
        {/* Recent Invoices */}
        <ActivityCard title="Recent Invoices" onViewAll={() => navigate('/envoice')} empty={!stats.recent_invoices?.length} emptyText="No invoices yet">
          {stats.recent_invoices?.slice(0, 4).map((inv, i) => (
            <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/[0.04] last:border-0 group cursor-pointer hover:bg-white/[0.02] -mx-1 px-1 rounded" onClick={() => navigate(`/invoices/${inv.id}`)}>
              <div className="min-w-0 flex-1 mr-3">
                <p className="text-sm text-white font-medium truncate">{inv.invoice_number || 'Draft'}</p>
                <p className="text-[10px] text-[#758A9F] truncate">{inv.client_name || 'No client'}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm text-white font-semibold tabular-nums">${(inv.total || 0).toFixed(2)}</p>
                <StatusBadge status={inv.status} />
              </div>
            </div>
          ))}
        </ActivityCard>

        {can('schedule') && (
          <ActivityCard title="Upcoming Events" onViewAll={() => navigate('/schedule')} empty={!stats.upcoming_events?.length} emptyText="No upcoming events">
            {stats.upcoming_events?.slice(0, 4).map((evt, i) => (
              <div key={i} className="flex items-center gap-2.5 py-2.5 border-b border-white/[0.04] last:border-0">
                <div className="w-1 h-8 rounded-full bg-[#B98A2C] flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white font-medium truncate">{evt.title}</p>
                  <p className="text-[10px] text-[#758A9F]">{evt.date} {evt.start_time && `at ${evt.start_time}`}</p>
                </div>
              </div>
            ))}
          </ActivityCard>
        )}

        {can('schedule') && (
          <ActivityCard title="Active Tasks" onViewAll={() => navigate('/schedule')} empty={!stats.recent_tasks?.length} emptyText="No active tasks">
            {stats.recent_tasks?.slice(0, 4).map((task, i) => (
              <div key={i} className="flex items-center gap-2.5 py-2.5 border-b border-white/[0.04] last:border-0">
                <div className={`w-1 h-8 rounded-full flex-shrink-0 ${
                  task.priority === 'urgent' ? 'bg-red-500' : task.priority === 'high' ? 'bg-amber-500' : 'bg-[#B98A2C]'
                }`} />
                <div className="min-w-0 flex-1">
                <p className="text-sm text-white font-medium truncate">{task.title}</p>
                <span className={`text-[10px] capitalize ${
                  task.priority === 'urgent' ? 'text-red-400' : task.priority === 'high' ? 'text-amber-400' : 'text-[#758A9F]'
                }`}>{task.priority}</span>
              </div>
            </div>
          ))}
        </ActivityCard>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, bg, onClick }) {
  return (
    <button data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`} onClick={onClick}
      className={`${bg} border border-white/[0.04] rounded-lg p-3.5 md:p-4 text-left hover:border-[#B98A2C]/20 transition-all group active:scale-[0.98] w-full`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={15} className={`${color} opacity-70`} />
        <span className="text-[9px] font-semibold tracking-[0.12em] uppercase text-[#758A9F]" style={{ fontFamily: "'Outfit', sans-serif" }}>{label}</span>
      </div>
      <p className={`text-xl md:text-2xl font-bold ${color} tabular-nums`} style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</p>
    </button>
  );
}

function ActivityCard({ title, onViewAll, empty, emptyText, children }) {
  return (
    <div className="bg-[#1B2838]/60 border border-white/[0.04] rounded-lg p-4 md:p-5 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
        <button onClick={onViewAll} className="text-[10px] text-[#B98A2C] hover:text-[#CBA33B] font-medium flex items-center gap-0.5 transition-colors">
          View all <ArrowRight size={10} />
        </button>
      </div>
      {empty ? <p className="text-sm text-[#758A9F] py-4 text-center">{emptyText}</p> : children}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    paid: 'bg-emerald-500/15 text-emerald-400',
    sent: 'bg-blue-500/15 text-blue-400',
    overdue: 'bg-red-500/15 text-red-400',
    draft: 'bg-white/[0.06] text-[#758A9F]',
  };
  return (
    <span className={`text-[8px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${styles[status] || styles.draft}`}>
      {status}
    </span>
  );
}
